import type {
    FormButton,
    FormField,
    FormFieldConditions,
    FormSection,
    FormTable,
    ReactiveDropdownDataSource,
    ReactiveDropdownDependencyBehaviour,
    ReactiveFieldConditionRule,
    ReactiveFieldLabelRule,
    ReactiveComputedField,
    ReactiveSectionVisibilityRule,
} from '../../interfaces/form.interface';
import type {
    DropdownConfiguration,
    Input,
    InputType,
    TextAreaConfiguration
} from '../../interfaces/input.interface';
import { InputType as ReactiveInputType } from '../../interfaces/input.interface';
import type { ReactiveFieldDependency } from '../dependencies/reactive-dependency-graph';

const DEFAULT_FIELD_WIDTH = 3;
const DEFAULT_SECTION_WIDTH = 12;
const DEFAULT_TEXTAREA_ROWS = 3;

export interface ReactiveFieldConditionsFactoryConfig {
    readonly rememberedOnDuplicate?: boolean;
    readonly required?: boolean;
    readonly retained?: boolean;
    readonly undoEnabled?: boolean;
    readonly visible?: boolean;
}

export type ReactiveFieldExtraConfig<TModel> = Partial<Omit<FormField<TModel>, 'conditions' | 'input' | 'label' | 'name' | 'order' | 'width'>>;

export interface ReactiveBaseFieldFactoryConfig<TModel> {
    readonly name: string;
    readonly label: string;
    readonly order: number;
    readonly width?: number;
    readonly isEditable?: boolean;
    readonly conditions?: ReactiveFieldConditionsFactoryConfig;
    readonly input?: Partial<Omit<Input<TModel>, 'isEditable' | 'type'>>;
    readonly visibleWhen?: ReactiveFieldConditionRule<TModel>;
    readonly readonlyWhen?: ReactiveFieldConditionRule<TModel>;
    readonly disabledWhen?: ReactiveFieldConditionRule<TModel>;
    readonly computed?: ReactiveComputedField<TModel>;
    readonly labelWhen?: ReactiveFieldLabelRule<TModel>;
    readonly dependencies?: readonly ReactiveFieldDependency[];
    readonly buttons?: FormButton[];
    readonly helpText?: string;
    readonly extra?: ReactiveFieldExtraConfig<TModel>;
}

export interface ReactiveGenericFieldFactoryConfig<TModel> extends ReactiveBaseFieldFactoryConfig<TModel> {
    readonly type: InputType;
}

export interface ReactiveTextFieldFactoryConfig<TModel> extends ReactiveBaseFieldFactoryConfig<TModel> {
    readonly maxLength?: number;
}

export interface ReactiveTextareaFieldFactoryConfig<TModel> extends ReactiveBaseFieldFactoryConfig<TModel> {
    readonly textAreaConfiguration?: TextAreaConfiguration;
}

export interface ReactiveHiddenFieldFactoryConfig<TModel> extends Omit<ReactiveBaseFieldFactoryConfig<TModel>, 'label'> {
    readonly label?: string;
    readonly defaultValue?: Input<TModel>['defaultValue'];
}

export interface ReactiveDropdownFieldFactoryConfig<TModel> extends ReactiveBaseFieldFactoryConfig<TModel> {
    readonly type?: InputType;
    readonly data?: Input<TModel>['data'];
    readonly defaultValue?: Input<TModel>['defaultValue'];
    readonly dataSource?: ReactiveDropdownDataSource<TModel>;
    readonly dependencyBehaviour?: ReactiveDropdownDependencyBehaviour;
    readonly dropdownConfiguration?: DropdownConfiguration<TModel>;
    readonly optionLabel?: string;
    readonly optionValue?: string;
}

export interface ReactiveSectionFactoryConfig<TModel> {
    readonly name?: string;
    readonly width?: number;
    readonly visible?: boolean;
    readonly visibleWhen?: ReactiveSectionVisibilityRule<TModel>;
    readonly fields: FormField<TModel>[];
    readonly order: number;
    readonly buttons?: FormButton[];
    readonly tables?: FormTable[];
}

/** Builds legacy-compatible field definitions from concise, typed factory options. */
export class ReactiveFieldFactory {
    public static create<TModel>(config: ReactiveGenericFieldFactoryConfig<TModel>): FormField<TModel> {
        return this.createWithInput(config, this.createInput(config.type, config));
    }

    public static createConditions(config: ReactiveFieldConditionsFactoryConfig = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: config.rememberedOnDuplicate ?? false,
            isRequired: config.required ?? false,
            isRetained: config.retained ?? false,
            hasUndoEnabled: config.undoEnabled ?? false,
            isVisible: config.visible ?? true,
        };
    }

    public static createWithInput<TModel>(config: ReactiveBaseFieldFactoryConfig<TModel>, input: Input<TModel>): FormField<TModel> {
        return {
            ...(config.extra ?? {}),
            name: config.name,
            label: config.label,
            input,
            ...(config.visibleWhen !== undefined ? { visibleWhen: config.visibleWhen } : {}),
            ...(config.readonlyWhen !== undefined ? { readonlyWhen: config.readonlyWhen } : {}),
            ...(config.disabledWhen !== undefined ? { disabledWhen: config.disabledWhen } : {}),
            ...(config.computed !== undefined ? { computed: config.computed } : {}),
            ...(config.labelWhen !== undefined ? { labelWhen: config.labelWhen } : {}),
            ...(config.dependencies !== undefined ? { dependencies: config.dependencies } : {}),
            ...(config.buttons !== undefined ? { buttons: config.buttons } : {}),
            ...(config.helpText !== undefined ? { helpText: config.helpText } : {}),
            width: config.width ?? DEFAULT_FIELD_WIDTH,
            conditions: this.createConditions(config.conditions),
            order: config.order,
        };
    }

    public static createInput<TModel>(type: InputType, config: ReactiveBaseFieldFactoryConfig<TModel>): Input<TModel> {
        return {
            type,
            isEditable: config.isEditable ?? true,
            ...(config.input ?? {}),
        };
    }
}

/** Factory for common editable text fields. */
export class ReactiveTextFieldFactory {
    public static create<TModel>(config: ReactiveTextFieldFactoryConfig<TModel>): FormField<TModel> {
        const input = ReactiveFieldFactory.createInput(ReactiveInputType.TEXT, {
            ...config,
            input: {
                ...(config.input ?? {}),
                ...(config.maxLength !== undefined ? { maxLength: config.maxLength } : {}),
            },
        });

        return ReactiveFieldFactory.createWithInput(config, input);
    }
}

/** Factory for editable textarea fields with safe default textarea capabilities disabled. */
export class ReactiveTextareaFieldFactory {
    public static create<TModel>(config: ReactiveTextareaFieldFactoryConfig<TModel>): FormField<TModel> {
        const textAreaConfiguration = config.textAreaConfiguration ?? {
            rows: DEFAULT_TEXTAREA_ROWS,
            hasVoiceTranscription: false,
            canTakePhoto: false,
            photoField: undefined,
        };
        const input = ReactiveFieldFactory.createInput(ReactiveInputType.TEXTAREA, {
            ...config,
            input: {
                ...(config.input ?? {}),
                textAreaConfiguration,
            },
        });

        return ReactiveFieldFactory.createWithInput(config, input);
    }
}

/** Factory for hidden fields that must keep legacy default values and computed metadata. */
export class ReactiveHiddenFieldFactory {
    public static create<TModel>(config: ReactiveHiddenFieldFactoryConfig<TModel>): FormField<TModel> {
        const input = ReactiveFieldFactory.createInput(ReactiveInputType.HIDDEN, {
            ...config,
            label: config.label ?? '',
            isEditable: config.isEditable ?? false,
            input: {
                ...(config.input ?? {}),
                ...(config.defaultValue !== undefined ? { defaultValue: config.defaultValue } : {}),
            },
        });

        return ReactiveFieldFactory.createWithInput({ ...config, label: config.label ?? '' }, input);
    }
}

/** Factory for dropdown-like fields, including search and multiselect variants. */
export class ReactiveDropdownFieldFactory {
    public static create<TModel>(config: ReactiveDropdownFieldFactoryConfig<TModel>): FormField<TModel> {
        const input = ReactiveFieldFactory.createInput(config.type ?? ReactiveInputType.DROPDOWN, {
            ...config,
            input: {
                ...(config.input ?? {}),
                ...(config.data !== undefined ? { data: config.data } : {}),
                ...(config.defaultValue !== undefined ? { defaultValue: config.defaultValue } : {}),
                ...(config.dropdownConfiguration !== undefined ? { dropdownConfiguration: config.dropdownConfiguration } : {}),
                ...(config.optionLabel !== undefined ? { optionLabel: config.optionLabel } : {}),
                ...(config.optionValue !== undefined ? { optionValue: config.optionValue } : {}),
            },
        });

        return {
            ...ReactiveFieldFactory.createWithInput(config, input),
            ...(config.dataSource !== undefined ? { dataSource: config.dataSource } : {}),
            ...(config.dependencyBehaviour !== undefined ? { dependencyBehaviour: config.dependencyBehaviour } : {}),
        };
    }
}

/** Builds standard sections without repeating the legacy width/visibility boilerplate. */
export class ReactiveSectionFactory {
    public static create<TModel>(config: ReactiveSectionFactoryConfig<TModel>): FormSection<TModel> {
        return {
            name: config.name ?? '',
            width: config.width ?? DEFAULT_SECTION_WIDTH,
            isVisible: config.visible ?? true,
            ...(config.visibleWhen !== undefined ? { visibleWhen: config.visibleWhen } : {}),
            fields: config.fields,
            ...(config.buttons !== undefined ? { buttons: config.buttons } : {}),
            ...(config.tables !== undefined ? { tables: config.tables } : {}),
            order: config.order,
        };
    }
}
